import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '../i18n/useDirection';

export interface ChatInputProps {
  /** Handler called when user submits a message */
  onSend: (message: string) => void;
  /** Whether the input should be disabled */
  disabled?: boolean;
  /** Whether a message is currently being streamed */
  isStreaming?: boolean;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Primary color for the send button */
  primaryColor?: string;
  /** Background color for the input container */
  containerBackgroundColor?: string;
  /** Whether to auto-focus the input on mount */
  autoFocus?: boolean;
  /** Pad the bottom by the device safe-area inset (fullscreen on notched phones). */
  safeAreaBottom?: boolean;
}

/**
 * Chat input component with send button.
 *
 * Features:
 * - Rounded-full text input styling
 * - Send button with customizable color
 * - Enter key to send (without Shift)
 * - Disabled state while sending/streaming
 * - Loading spinner while streaming
 * - Auto-focus on mount
 */
export function ChatInput({
  onSend,
  disabled = false,
  isStreaming = false,
  placeholder,
  primaryColor = '#3b82f6',
  containerBackgroundColor = '#ffffff',
  autoFocus = true,
  safeAreaBottom = false,
}: ChatInputProps) {
  const { t } = useTranslation('widget');
  const { isRTL } = useDirection();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const hasAutoFocused = useRef(false);

  // Use ref callback to focus on mount
  const setInputRef = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (node && autoFocus && !hasAutoFocused.current) {
      node.focus();
      hasAutoFocused.current = true;
    }
  }, [autoFocus]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedValue = inputValue.trim();
      if (trimmedValue && !disabled && !isStreaming) {
        onSend(trimmedValue);
        setInputValue('');
      }
    },
    [inputValue, disabled, isStreaming, onSend]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  const isDisabled = disabled || isStreaming;
  const canSend = !isDisabled && inputValue.trim().length > 0;

  return (
    <form
      onSubmit={handleSubmit}
      className="p-3 border-t border-gray-200 bg-white shrink-0"
      style={{
        backgroundColor: containerBackgroundColor,
        ...(safeAreaBottom ? { paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' } : {}),
      }}
    >
      {/* `dir` on the widget root already puts the send button on the reading-end side; reversing
          the row again would drag it back to the right in a mirrored chat. */}
      <div className="flex gap-2">
        <input
          ref={setInputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? t('input.placeholder')}
          // What the visitor writes decides how it reads, not how the chrome is laid out. Inheriting
          // `rtl` here pushed the placeholder's trailing "..." to the front of the line and did the
          // same to anything Latin they typed.
          dir="auto"
          disabled={isDisabled}
          className="flex-1 px-4 py-2 rounded-full border border-gray-300 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          style={{
            flex: 1,
            padding: '8px 16px',
            borderRadius: '9999px',
            border: '1px solid #d1d5db',
            outline: 'none',
            fontSize: '14px',
            // The field is always white, so what the visitor types is always black: no club color
            // reaches this surface, and none can make their own typing hard to read.
            backgroundColor: 'white',
            color: '#000000',
            // `dir="auto"` above decides the character order from the content; which edge the text
            // sits against is a layout question, so it follows the club's setting.
            textAlign: isRTL ? 'right' : 'left',
          }}
          aria-label={t('input.messageLabel')}
        />
        <button
          type="submit"
          disabled={!canSend}
          className="px-4 py-2 text-white rounded-full font-medium transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          style={{ backgroundColor: primaryColor }}
          aria-label={t('input.sendLabel')}
        >
          {/* The paper plane points the way the message travels, so it mirrors with the layout.
              The spinner does not: a rotating indicator has no reading direction. */}
          {isStreaming ? <LoadingIcon /> : <SendIcon mirrored={isRTL} />}
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// Icons
// ============================================================================

function SendIcon({ mirrored = false }: { mirrored?: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-5 h-5"
      aria-hidden="true"
      data-mirrored={mirrored ? 'true' : undefined}
      style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
    >
      <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
    </svg>
  );
}

function LoadingIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-5 h-5 animate-spin"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H3.989a.75.75 0 00-.75.75v4.242a.75.75 0 001.5 0v-2.43l.31.31a7 7 0 0011.712-3.138.75.75 0 00-1.449-.39zm-6.624-6.85a5.5 5.5 0 019.201-2.466l.312.311h-2.433a.75.75 0 000 1.5h4.243a.75.75 0 00.75-.75V.927a.75.75 0 00-1.5 0v2.43l-.31-.31a7 7 0 00-11.712 3.138.75.75 0 001.449.39z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default ChatInput;
